import { GridLabelProps } from '@nivo/radar';
import { RadarLayerId } from '@nivo/radar';
import { ResponsiveRadar } from '@nivo/radar';
import React, { useEffect, useMemo, useRef } from 'react';
import gradients from 'apps/sigma2-ui/lib/design-tokens/gradients';
import { KARLA_FONT_FAMILY } from 'libs/end-to-end/src/types/branding/fonts';

interface GradientDefColor {
  offset: number;
  color: string;
  opacity?: number;
}

interface GradientDef {
  id: string;
  type: string;
  colors: GradientDefColor[];
}

export const RADAR_CHART_LABEL_OFFSET = 28;
export const RADAR_CHART_MARGIN_X = 150;

const GradientDefs = (gradientDefs: GradientDef[]) => {
  const nodes = useMemo(() => {
    for (const gradientDef of gradientDefs) {
      return (
        <radialGradient
          gradientUnits="userSpaceOnUse"
          cx="0"
          cy="0"
          id={gradientDef.id}
          key={gradientDef.id}
        >
          {gradientDef.colors.map((colorDef) => (
            <stop
              offset={colorDef.offset}
              stopColor={colorDef.color}
              opacity={colorDef.opacity ?? 0.5}
              key={colorDef.offset}
            />
          ))}
        </radialGradient>
      );
    }
  }, [gradientDefs]);

  return <defs>{nodes}</defs>;
};

export interface LevelLabelsConfig {
  showLevelLabels: boolean;
  labelFill?: string;
  labelXOffset?: number;
  labelYOffset?: number;
  fontSize?: string;
}

interface LevelLabelTextContent {
  ring0: string;
  ring1: string;
  ring2: string;
  ring3: string;
  ring4: string;
  ring5: string;
  ring6: string;
}

/**
 * This helper function abtracts away attaching level labels to the nivo Radar Chart.
 * It calculates the position of the 2nd, 4th, and 6th ring in order to find where to place the Low, Medium, and High labels
 *
 * @param graphGroup - SVG group that is the first child of the Radar Chart SVG. See custom useRadarGradientFills hook for more details
 * @param levelLabelsConfig - Configuration values for customizing the level labels
 */
// TODO: The complexity of this code is greater than 10. Refer to https://eslint.org/docs/latest/rules/complexity
const generateLevelLabels = (
  graphGroup: SVGElement,
  levelLabelsConfig: LevelLabelsConfig,
  levelLabelTextContent: LevelLabelTextContent,
) => {
  // In order use the same coordinate system as the rest of the graph, we will steal the transformation parameter from a nearby group
  const translateParam =
    (graphGroup.childNodes[0] as Element).getAttribute('transform') || '';

  // The baseChartGroup holds the SVG elements for the Radar Chart spokes, spoke labels, and rings
  const baseChartGroup = graphGroup.firstChild?.childNodes;

  if (!baseChartGroup) {
    return;
  }

  // We extract the ring/circle elements from the baseChartGroup and find the radius value of the innermost ring
  const circleElements = Array.from(
    baseChartGroup as NodeListOf<Element>,
  ).filter((el) => {
    return el.nodeName === 'circle';
  });

  const radiusList = circleElements.map((circle) =>
    Number(circle.getAttribute('r')),
  );
  const innerRingRadius: number = Math.min(...radiusList);

  const fontSize = levelLabelsConfig.fontSize ?? '12px';
  const labelFill = levelLabelsConfig.labelFill ?? '#14181C'; // gray/1000
  const labelX = levelLabelsConfig.labelXOffset ?? 2;
  const labelYOffset = levelLabelsConfig.labelYOffset ?? 2;

  // From here, we can calculate the Y coordinates of the edges of the 2nd, 4th, and 6th rings
  const ringNeg1Y = -1 * innerRingRadius;

  const ringY: Record<keyof LevelLabelTextContent, number> = {
    ring0: 0 - labelYOffset,
    ring1: ringNeg1Y - labelYOffset,
    ring2: 2 * ringNeg1Y - labelYOffset,
    ring3: 3 * ringNeg1Y - labelYOffset,
    ring4: 4 * ringNeg1Y - labelYOffset,
    ring5: 5 * ringNeg1Y - labelYOffset,
    ring6: 6 * ringNeg1Y - labelYOffset,
  };

  // We need to create or update the levelLabelGroup element.
  let levelLabelGroup = graphGroup.querySelector('#levelLabelGroup');

  if (!levelLabelGroup) {
    levelLabelGroup = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'g',
    );

    graphGroup.appendChild(levelLabelGroup);
  }

  // Set the attributes for the levelLabelGroup
  levelLabelGroup.setAttributeNS(null, 'id', 'levelLabelGroup');
  levelLabelGroup.setAttributeNS(null, 'transform', translateParam);
  levelLabelGroup.setAttributeNS(null, 'data-qa', 'radarChartLevelLabels');

  const makeLabel = (ringKey: keyof LevelLabelTextContent) => {
    if (!levelLabelGroup) {
      return;
    }

    let ringLabel = levelLabelGroup.querySelector(`#${ringKey}Label`);

    if (!ringLabel) {
      ringLabel = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'text',
      );
      levelLabelGroup.append(ringLabel);
    }

    ringLabel.setAttributeNS(null, 'id', `${ringKey}Label`);
    ringLabel.setAttributeNS(null, 'data-qa', `${ringKey}Label`);
    ringLabel.setAttributeNS(null, 'x', `${labelX}`);
    ringLabel.setAttributeNS(null, 'y', `${ringY[ringKey]}`);
    ringLabel.setAttributeNS(null, 'fill', labelFill);
    ringLabel.setAttributeNS(null, 'font-size', fontSize);
    ringLabel.textContent = levelLabelTextContent[ringKey];
  };

  makeLabel('ring0');
  makeLabel('ring1');
  makeLabel('ring2');
  makeLabel('ring3');
  makeLabel('ring4');
  makeLabel('ring5');
  makeLabel('ring6');
};

/**
 * This hook is a temporary workaround to allow radar chart layers to use
 * gradient fills. The Nivo component library is working on adding gradient
 * support to its components, but Radar only supports colors, via the `colors`
 * prop. It also modifies the chart to support named level labels.
 *
 * @param radarComponentProps - Raw props passed to the RadarChart components
 * @param gradientDefs - An array of gradient definitions, in order they should be applied to layers
 * @param levelLabelDescriptions - Object containing the text strings for Low, Medium, and High
 */
function useRadarSvgCustomizations<E>(
  radarComponentProps: RadarChartProps,
  gradientDefs: GradientDef[],
  levelLabelTextContent: LevelLabelTextContent,
) {
  const wrapperElRef = useRef<E>(null);

  useEffect(() => {
    if (!gradientDefs || !gradientDefs.length) {
      return;
    }

    const wrapperEl = wrapperElRef.current;
    const gradientDefIds = gradientDefs.map((g) => g.id);

    if (!wrapperEl || !(wrapperEl instanceof HTMLElement)) {
      return;
    }

    // TODO: The complexity of this code is greater than 10. Refer to https://eslint.org/docs/latest/rules/complexity
    const updateSvgLayout = () => {
      const svgEl = wrapperEl.querySelector('svg');

      if (!svgEl) {
        return;
      }

      const defsEl = svgEl.querySelector('defs');

      if (!defsEl) {
        return;
      }

      const layersGroup = defsEl.nextElementSibling;

      if (!layersGroup) {
        return;
      }

      const ringsGroup = layersGroup.nextElementSibling;

      if (!ringsGroup) {
        return;
      }

      const graphGroup = svgEl.querySelector('g');

      if (!graphGroup) {
        return;
      }

      const layers = Array.from(layersGroup.querySelectorAll('path'));

      for (const layer of layers) {
        const gradientDefId = gradientDefIds[layers.indexOf(layer)];

        if (!gradientDefId) {
          continue;
        }

        const gradientDefEl = document.getElementById(gradientDefId);

        const newFill = `url(#${gradientDefId})`;

        // This is not the right place to put this. If we want to keep it here, we need to guard
        // against it being called multiple times.
        if (radarComponentProps.levelLabelsConfig.showLevelLabels) {
          generateLevelLabels(
            graphGroup,
            radarComponentProps.levelLabelsConfig,
            levelLabelTextContent,
          );
        }

        if (gradientDefEl) {
          // Set the gradient radius to half the width of the outermost ring
          const gradientRadius = ringsGroup.getBoundingClientRect().width / 2;

          gradientDefEl.setAttribute('r', String(gradientRadius));
          layer.setAttribute('fill', newFill);
        } else {
          throw new Error(`Expected a <radialGradent id="${gradientDefId}"> `);
        }
      }
    };

    const interval = setInterval(() => {
      updateSvgLayout();
    }, 50);

    return () => {
      clearInterval(interval);
    };
  }, [gradientDefs, levelLabelTextContent, radarComponentProps]);

  const defaultLayers = [
    'grid',
    'layers',
    'slices',
    'dots',
    'legends',
  ] as RadarLayerId[];
  const layersClone = [...(radarComponentProps.layers || defaultLayers)];

  layersClone.splice(1, 0, GradientDefs.bind(null, gradientDefs));

  return {
    wrapperElRef,
    modifiedProps: {
      blendMode: 'normal' as const,
      colors: ['transparent'],
      fillOpacity: 0.5,
      layers: layersClone,
    },
  };
}

export type RadarChartProps = Parameters<typeof ResponsiveRadar>[0] & {
  gradientFills: string[];
  levelLabelsConfig: LevelLabelsConfig;
  dataTestId?: string;
};

const GridLabel = ({
  id,
  x,
  y,
  anchor,
  color,
}: GridLabelProps & {
  color: string;
}) => {
  const titleCased = useMemo(
    () =>
      id
        .split(' ')
        .map((word) => {
          if (word === 'and' || word === 'of') {
            return word;
          }

          return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' '),
    [id],
  );

  const ref = useRef<SVGTextElement>(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    textEllipsis(
      ref.current,
      titleCased,
      RADAR_CHART_MARGIN_X - RADAR_CHART_LABEL_OFFSET,
    );
  }, [titleCased]);
  return (
    <g transform={`translate(${x}, ${y})`}>
      <title>{titleCased}</title>
      <text
        dominantBaseline="central"
        textAnchor={anchor}
        style={{
          fontFamily: KARLA_FONT_FAMILY,
          fontSize: '16px',
          fontWeight: 'bold',
          fill: color,
        }}
        ref={ref}
      />
    </g>
  );
};

const textEllipsis = (el: SVGTextElement, text: string, width: number) => {
  if (text.length === 0) {
    el.textContent = text;
    return;
  }

  const mechanism1 = textEllipse_GetSubStringLengthMechanism(el, text, width);

  if (mechanism1) {
    return;
  }

  const mechanism2 = textEllipse_GetComputedTextLengthMechanism(
    el,
    text,
    width,
  );

  if (mechanism2) {
    return;
  }

  textEllipse_FallbackMechanism(el, text, width);
};

const textEllipse_GetSubStringLengthMechanism = (
  el: SVGTextElement,
  text: string,
  width: number,
): boolean => {
  if (typeof el.getSubStringLength === 'undefined') {
    return false;
  }

  el.textContent = text;
  let len = text.length;
  try {
    while (el.getSubStringLength(0, len--) > width) {
      el.textContent = text.slice(0, len) + '...';
    }
  } catch {
    return false;
  }

  return true;
};

const textEllipse_GetComputedTextLengthMechanism = (
  el: SVGTextElement,
  text: string,
  width: number,
): boolean => {
  if (typeof el.getComputedTextLength === 'undefined') {
    return false;
  }
  try {
    while (el.getComputedTextLength() > width) {
      text = text.slice(0, -1);
      el.textContent = text + '...';
    }
  } catch {
    // restore the original text
    el.textContent = text;
    return false;
  }

  return true;
};

const textEllipse_FallbackMechanism = (
  el: SVGTextElement,
  text: string,
  width: number,
): boolean => {
  try {
    while (el.getBBox().width > width) {
      text = text.slice(0, -1);
      // we need to update the textContent to update the boundary width
      el.textContent = text + '...';
    }
  } catch {
    // restore the original text
    el.textContent = text;
    return false;
  }

  return true;
};

export const RadarChart: React.FC<RadarChartProps> = (props) => {
  // Ensure each gradient definition has an `id` property
  const gradientFills = useMemo(() => {
    const fills: GradientDef[] = [];

    for (const gradientId of props.gradientFills || []) {
      const gradient = gradients.find((g) => g.id === gradientId);
      if (!gradient) {
        continue;
      }
      fills.push(gradient);
    }
    return fills;
  }, [props]);

  const levelLabelDescriptions: LevelLabelTextContent = useMemo(() => {
    // We are not showing the labels in the polar chart for now.
    return {
      ring0: '',
      ring1: '',
      ring2: '',
      ring3: '',
      ring4: '',
      ring5: '',
      ring6: '',
    };
  }, []);

  // TODO: Rerun this workaround in response to chart resizing
  const { wrapperElRef, modifiedProps } =
    useRadarSvgCustomizations<HTMLDivElement>(
      props,
      gradientFills,
      levelLabelDescriptions,
    );

  return (
    <div
      ref={wrapperElRef}
      data-qa="radarChart"
      style={{
        position: 'absolute',
        left: '0',
        top: '0',
        width: '100%',
        height: '100%',
      }}
      data-testid={props.dataTestId}
    >
      <ResponsiveRadar
        {...props}
        {...modifiedProps}
        gridLabelOffset={RADAR_CHART_LABEL_OFFSET}
        gridLabel={(params) => (
          <GridLabel
            {...params}
            color={props.theme?.text?.color ?? 'rgb(41, 45, 51)'}
          />
        )}
        margin={{
          top: 10,
          right: RADAR_CHART_MARGIN_X,
          bottom: 10,
          left: RADAR_CHART_MARGIN_X,
        }}
      />
    </div>
  );
};
